/**
 * PWA: instalação, atualização e notificações (plano, 19.4).
 * - Android: evento beforeinstallprompt → botão próprio "Instalar app".
 * - iPhone: sem botão automático → guia Compartilhar → Adicionar à Tela de Início.
 */
import type { ApiClient } from '@lindeza/core';
import { create } from 'zustand';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaState {
  installEvent: BeforeInstallPromptEvent | null;
  updateReady: boolean;
  applyUpdate: (() => void) | null;
}

export const usePwa = create<PwaState>(() => ({ installEvent: null, updateReady: false, applyUpdate: null }));

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function initPwa(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    usePwa.setState({ installEvent: e as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => usePwa.setState({ installEvent: null }));

  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    void import('workbox-window').then(({ Workbox }) => {
      const wb = new Workbox('/sw.js', { scope: '/' });
      wb.addEventListener('waiting', () => {
        usePwa.setState({
          updateReady: true,
          applyUpdate: () => {
            wb.addEventListener('controlling', () => window.location.reload());
            wb.messageSkipWaiting();
          },
        });
      });
      void wb.register();
    });
  }
}

export async function promptInstall(): Promise<boolean> {
  const ev = usePwa.getState().installEvent;
  if (!ev) return false;
  await ev.prompt();
  const choice = await ev.userChoice;
  usePwa.setState({ installEvent: null });
  return choice.outcome === 'accepted';
}

/** Conta aberturas do app para convidar a instalar no momento certo (depois do 2º uso). */
export function countOpen(): number {
  try {
    const n = Number(localStorage.getItem('lindeza:opens') ?? '0') + 1;
    localStorage.setItem('lindeza:opens', String(n));
    return n;
  } catch {
    return 1;
  }
}

export function openCount(): number {
  try {
    return Number(localStorage.getItem('lindeza:opens') ?? '0');
  } catch {
    return 0;
  }
}

export function dismissedInstall(): boolean {
  try {
    return localStorage.getItem('lindeza:install-dismissed') === '1';
  } catch {
    return false;
  }
}

export function dismissInstall(): void {
  try {
    localStorage.setItem('lindeza:install-dismissed', '1');
  } catch {
    // modo privado
  }
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** "Quer ser avisada quando um cliente agendar?" */
export async function enablePush(api: ApiClient): Promise<'ok' | 'denied' | 'unsupported' | 'no-key'> {
  if (!pushSupported()) return 'unsupported';
  const { public_key } = await api.vapidKey();
  if (!public_key) return 'no-key';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(public_key) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.pushSubscribe({ endpoint: json.endpoint, keys: json.keys });
  return 'ok';
}

export async function pushEnabled(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return Boolean(await reg?.pushManager.getSubscription());
  } catch {
    return false;
  }
}
